import React, { useEffect, useRef, useState } from 'react';

const ORGS = [
  {
    name: 'Techstars',
    program: 'Techstars Tulsa',
    desc: 'Global network accelerating early-stage founders and startups',
    logo: '/assets/techstars-logo.png',
  },
  {
    name: 'Morehouse College',
    program: 'Morehouse MIEC',
    desc: 'Fostering entrepreneurship and innovation in emerging technologies',
    logo: '/assets/morehouse-logo.png',
  },
  {
    name: 'UConn',
    program: 'UConn CCEI',
    desc: 'Connecticut Center for Entrepreneurship and Innovation',
    logo: '/assets/uconn-logo.png',
  },
  {
    name: 'Georgia Tech',
    program: 'Georgia Tech Create-X',
    desc: 'Supporting innovation in health technology and entrepreneurship',
    logo: '/assets/georgia-tech-logo.png',
  },
];

function BackedBy() {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visible, setVisible] = useState<boolean[]>(() => ORGS.map(() => false));

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setVisible(ORGS.map(() => true));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idx = cardRefs.current.indexOf(entry.target as HTMLDivElement);
          if (idx !== -1) {
            setVisible((prev) => {
              if (prev[idx]) return prev;
              const next = [...prev];
              next[idx] = true;
              return next;
            });
          }
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.2 }
    );

    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-16 md:py-24 bg-cream-hi">
      <div className="max-w-[1180px] mx-auto px-6 md:px-8">
        <div className="text-center max-w-[600px] mx-auto mb-14">
          <h2 className="font-display text-[1.9rem] md:text-[2.2rem] font-bold text-navy-deep">
            Backed by <span className="text-sky">Leading Organizations</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {ORGS.map((org, i) => (
            <div
              key={org.name}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              className={`text-center space-y-3 transition-all duration-700 ease-out motion-reduce:!opacity-100 motion-reduce:!translate-y-0 motion-reduce:transition-none ${
                visible[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: visible[i] ? `${i * 100}ms` : '0ms' }}
            >
              <div className="flex justify-center mb-2">
                <img src={org.logo} alt={org.name} className="h-16 md:h-20 w-auto object-contain" />
              </div>
              <h3 className="font-display text-lg font-semibold text-navy-deep">{org.name}</h3>
              <p className="text-xs uppercase tracking-wider text-navy/60">{org.program}</p>
              <p className="text-sm text-navy leading-relaxed">{org.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default BackedBy;
